import { createContext, useContext, useState } from 'react'

import { modalTypes } from '@/constants/modalTypes'

import type { AlertStyleModalConfig } from '@/components/Modal/Layouts/Alert'
import type { CollectionFiltersModalConfig } from '@/components/Modal/Templates/CollectionFilters'
import type { ContentDetailsModalConfig } from '@/components/Modal/Templates/ContentDetails'
import type { ContentSuggestionModalConfig } from '@/components/Modal/Templates/ContentSuggestion'
import type { CreateFolderCollectionModalConfig } from '@/components/Modal/Templates/CreateFolderCollectionModal'
import type { DeleteCollectionModalConfig } from '@/components/Modal/Templates/DeleteCollection'
import type { MoveCollectionModalConfig } from '@/components/Modal/Templates/MoveCollection'
import type { MoveCollectionDropModalConfig } from '@/components/Modal/Templates/MoveCollectionDrop'
import type { NewContentModalConfig } from '@/components/Modal/Templates/NewContentModal'
import type { RenameCollectionModalConfig } from '@/components/Modal/Templates/RenameCollection'
import type { ShareCollectionModalConfig } from '@/components/Modal/Templates/ShareCollection'

type AddToCollectionModalConfig = {
  contentId: string
  contentName: string
  contentImageUrl: string
}

type BannerVideoModalConfig = {
  videoSrc: string
  videoHandler: (src: string) => void
}

type EditAvatarModalConfig = {
  loadHandler: (isLoading: boolean) => void
}

type EditDataModalConfig = {
  loadHandler: (isLoading: boolean) => void
}

type EditGuardianDataModalConfig = {
  guardianName: string
  guardianID: string
  minorName: string
}

type EditLanguageModalConfig = {
  loadHandler: (isLoading: boolean) => void
}

type EditPasswordModalConfig = {
  loadHandler: (isLoading: boolean) => void
}

type ViewerHelpModalConfig = {
  type?: 'models' | 'blades' | null
}

type ConfigMap = {
  [modalTypes.add_to_collection]: AddToCollectionModalConfig
  [modalTypes.alert]: AlertStyleModalConfig
  [modalTypes.banner_video]: BannerVideoModalConfig
  [modalTypes.collection_filters]: CollectionFiltersModalConfig
  [modalTypes.content_details]: ContentDetailsModalConfig
  [modalTypes.content_suggestion]: ContentSuggestionModalConfig
  [modalTypes.create_collection_folder]: CreateFolderCollectionModalConfig
  [modalTypes.delete_account]: undefined
  [modalTypes.delete_collection]: DeleteCollectionModalConfig
  [modalTypes.edit_avatar]: EditAvatarModalConfig
  [modalTypes.edit_data]: EditDataModalConfig
  [modalTypes.edit_guardian_data]: EditGuardianDataModalConfig
  [modalTypes.edit_language]: EditLanguageModalConfig
  [modalTypes.edit_password]: EditPasswordModalConfig
  [modalTypes.move_collection]: MoveCollectionModalConfig
  [modalTypes.move_collection_drop]: MoveCollectionDropModalConfig
  [modalTypes.new_content]: NewContentModalConfig
  [modalTypes.rename_collection]: RenameCollectionModalConfig
  [modalTypes.share_collection]: ShareCollectionModalConfig
  [modalTypes.viewer_help]: ViewerHelpModalConfig
}

type ModalParams<T extends modalTypes> = {
  type: T
  classes?: string
  config?: ConfigMap[T]
}

interface ModalContextType {
  showModal: boolean
  settings: ModalParams<modalTypes>
  MODAL_TYPES: typeof modalTypes
  callModal: <T extends modalTypes>(settings: ModalParams<T>) => void
  closeModal: () => void
}

export const ModalContext = createContext<ModalContextType>(
  {} as ModalContextType
)

interface ModalProviderProps {
  children: React.ReactNode
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [showModal, setShowModal] = useState<boolean>(false)
  const [settings, setSettings] = useState<ModalParams<modalTypes>>()

  const callModal = <T extends modalTypes>(settings: ModalParams<T>) => {
    setSettings(settings)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
  }

  return (
    <ModalContext.Provider
      value={{
        showModal,
        settings,
        MODAL_TYPES: modalTypes,
        callModal,
        closeModal
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}

export function useModal() {
  return useContext(ModalContext)
}
